// ─────────────────────────────────────────────────────────────────
// List variables — what a list block binds to, and what its item scope offers
// ----------------------------------------------------------------
// A `list` variable is a container, not a value: `vars` holds the fields one
// item carries, and its own `default` is the sample item the canvas renders
// with (see packages/shared/src/variable_definitions.ts). The rules below are
// the editor's reading of that shape. They live in one module — rather than
// inside the renderer that happens to use them first — because the inspector,
// the canvas and the render/send paths all have to agree on them.
// ─────────────────────────────────────────────────────────────────
import { parseVariableDefinitions, type Block, type VariableDefinition } from '@blogforge/shared';
import type { DragPath } from '../types.js';

/** A list variable, which is a container rather than a value. */
export type ListVariableDefinition = Extract<VariableDefinition, { type: 'list' }>;

/**
 * How a list block's `dataBindingKey` resolved against the document. A broken
 * binding is a normal state — the key is picked before the variable exists, and
 * the variable can be renamed or deleted afterwards — so the reason travels with
 * the failure instead of being a thrown error.
 */
export type ListBinding =
  | { ok: true; definition: ListVariableDefinition }
  | { ok: false; problem: string };

/**
 * The names a `{{…}}` may interpolate: every definition that holds a value. A
 * list holds variables instead, so its own key is never one of them — a token
 * that resolved to a whole list would have nothing to print. List *fields* are
 * not definitions on the document, so they are not here either; they enter
 * scope inside the item template (see `itemScopeDefinitions`).
 */
export function valueVariableNames(raw: unknown): string[] {
  return Object.entries(parseVariableDefinitions(raw))
    .filter(([, definition]) => definition.type !== 'list')
    .map(([name]) => name);
}

/**
 * Resolve a list block's `dataBindingKey`. The binding is broken when it names
 * nothing on the document, or names something that is not a list — both are
 * worth saying differently, because only one of them is a typo.
 */
export function resolveListBinding(raw: unknown, dataBindingKey: string): ListBinding {
  const key = dataBindingKey.trim();
  if (key === '') return { ok: false, problem: 'No list variable is bound yet.' };

  const definition = parseVariableDefinitions(raw)[key];
  if (!definition) return { ok: false, problem: `This email defines no variable named "${key}".` };
  if (definition.type !== 'list') {
    return { ok: false, problem: `"${key}" is a ${definition.type} variable, not a list.` };
  }
  return { ok: true, definition };
}

/** The fields one item carries, in name order. */
export function listItemNames(definition: ListVariableDefinition): string[] {
  return Object.keys(definition.vars ?? {}).sort();
}

/**
 * The item editor-time rendering uses: the list's own `default[0]`. `null` when
 * the list carries no sample item, which leaves the item template without
 * values — the fields are still known, from `vars`.
 */
export function listSampleItem(definition: ListVariableDefinition): Record<string, unknown> | null {
  const [first] = definition.default ?? [];
  if (!first || typeof first !== 'object' || Array.isArray(first)) return null;
  return first as Record<string, unknown>;
}

/**
 * The document's list variables, in name order — what a binding picker offers.
 * A list block repeats items, and a `list` is the only definition holding any.
 */
export function listVariableKeys(raw: unknown): string[] {
  return Object.entries(parseVariableDefinitions(raw))
    .filter(([, definition]) => definition.type === 'list')
    .map(([name]) => name)
    .sort();
}

/**
 * The definitions in scope inside `itemBlock`: the document's own, with the
 * list's fields layered over them, so a field shadows an outer variable of the
 * same name. Reading it as a plain definitions record is what keeps the item
 * subtree's pickers, default previews and highlightable names on the same set
 * of names — and makes the scope a property of the subtree it is passed to,
 * rather than of the whole document.
 *
 * A list field can never be a list itself (the schema refuses one), so nothing
 * here can deepen the nesting.
 */
export function itemScopeDefinitions(
  raw: unknown,
  definition: ListVariableDefinition,
): Record<string, unknown> {
  return { ...parseVariableDefinitions(raw), ...(definition.vars ?? {}) };
}

/**
 * The definitions a block resolves against, given where it sits.
 *
 * The canvas hands the item scope down its own tree (ListRenderer passes it as
 * the subtree's definitions), but a surface that reads the *document* rather
 * than rendering it — the inspector, whose pickers cover the selected block —
 * has to work the scope out from the selection's path instead. This is that
 * rule, in one place: outside a list it is the document alone, and inside one it
 * is the document with that list's fields layered over it.
 *
 * `blockAt` reads the container a path segment names (the canvas has the tree,
 * the inspector the store), because a segment carries the list *block's* id, and
 * the variable it binds to lives on the block.
 */
export function scopedDefinitions(
  raw: unknown,
  path: DragPath,
  blockAt: (id: string, ancestors: DragPath) => Block | null,
): Record<string, unknown> {
  // A list cannot hold a list, so at most one segment on a path is one: the
  // list whose item template the block sits in.
  const index = path.findIndex((segment) => segment.type === 'list');
  if (index === -1) return parseVariableDefinitions(raw);

  const segment = path[index]!;
  const listBlock = blockAt(segment.id, path.slice(0, index));
  const key = (listBlock?.attrs as { dataBindingKey?: unknown } | undefined)?.dataBindingKey;
  const binding = resolveListBinding(raw, typeof key === 'string' ? key : '');

  return binding.ok ? itemScopeDefinitions(raw, binding.definition) : parseVariableDefinitions(raw);
}
